import { OccurrenceStepEnum } from "../../../../../enums/occurrenceStep";
import {
  Occurrence,
  OccurrenceModule,
  OccurrencePartOrder,
  OccurrencePartOrderPart,
  OccurrencePartOrderDeliveryDate,
} from "../../../../../models/occurrence";
import { OccurrenceFailureDto } from "../../../../../dtos/occurrenceDto";

const toLocalDateTimeString = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  const ms = String(date.getMilliseconds()).padStart(3, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${ms}`;
};

export const occurrenceToJson = (occurrence: Occurrence): any => {
  return {
    vehicle: {
      assetId: occurrence.vehicleId,
      chassis: occurrence.vehicleChassis,
      model: occurrence.model ?? occurrence.vehicleModel,
      licensePlate: occurrence.vehicleLicensePlate,
      vehicleType: occurrence.vehicleType,
      name: occurrence.vehicleName ?? "-",
      vehicleYear: occurrence.vehicleYear,
      orderDate: occurrence.osDtOpenAt ? toLocalDateTimeString(occurrence.osDtOpenAt) : undefined,
      odometer: occurrence.vehicleOdometer,
      hourMeter: occurrence.vehicleHourmeter,
      emissionStandard: occurrence.emissionStandard,
      payloadType: occurrence.vehiclePayloadType,
      maximumPayload: occurrence.vehicleMaximumPayload,
      criticalPayload: occurrence.vehicleCriticalPayload,
      stopped: occurrence.vehicleStopped,
    },
    driver: {
      name: occurrence.driverName,
      driverLicenseNumber: occurrence.driverLicenseNumber,
      phone: occurrence.driverPhone,
      checkInDriver: occurrence.driverCheckInDriver,
    },
    dealership: {
      dn: occurrence.dealershipDn,
      name: occurrence.dealershipName,
      regional: occurrence.dealershipRegional,
      cell: occurrence.dealershipCell,
      cnpj: occurrence.dealershipUniqueId,
      local: occurrence.dealershipLocation,
      representative: occurrence.dealershipRepresentative,
      cellNumber: occurrence.dealershipPhone,
      area: occurrence.dealershipDemarcatedArea,
    },
    observation: occurrence.observation ?? "-",
    status: occurrence.status ?? "OPEN",
    version: occurrence.version,
    criticality: occurrence.criticality ?? 1,
    osNumber: occurrence.osNumber,
    osDtOpenAt: occurrence.osDtOpenAt ? toLocalDateTimeString(occurrence.osDtOpenAt) : undefined,
    renter: occurrence.renter,
    literatureTroubleshooting: occurrence.literatureTroubleshooting,
    country: occurrence.country,
    occurrenceType: occurrence.occurrenceType ?? "P",
    tasNumber: occurrence.tasNumber,
    tasStatus: occurrence.tasStatus,
    timeOpenProtocol: occurrence.timeOpenProtocol || null,
    source: occurrence.source,
    hasLink: occurrence.hasLink,
    mainOccurrence: occurrence.mainOccurrence,
    estimateTimeRepair: occurrence.estimateTimeRepair ?? "00:00",
    solutionProposed: occurrence.solutionProposed,
    mechanicLocation: occurrence.mechanicLocation,
    towTruckLocation: occurrence.towTruckLocation,
    checklist: occurrence.checklist,
    customer: occurrence.customer,
    dtcs: occurrence.modules?.map((module: OccurrenceModule) => ({
      name: module.name,
      spn: module.spn,
      fmi: module.fmi,
    })),
    occurrenceStep: {
      expectedEndDate:
        occurrence.steps?.find((step) => step.dtStart && !step.dtEnd)
          ?.expectedEndDate?.toISOString() ?? "",
      observation:
        occurrence.steps?.find((step) => step.dtStart && !step.dtEnd)
          ?.observation ?? "",
      report:
        occurrence.steps?.find((step) => step.dtStart && !step.dtEnd)?.report ??
        "",
      estimatedTime:
        occurrence.steps?.find((step) => step.dtStart && !step.dtEnd)
          ?.estimatedTime ?? "00:00",
    },
    partOrders: occurrence.partOrders?.map((part: OccurrencePartOrder) => ({
      number: part.number,
      status: part.status,
      date: part.date?.toISOString(),
    })),
    partOrderDeliveryDates: occurrence.partOrderDeliveryDates?.map((part: OccurrencePartOrderDeliveryDate) => ({
      expected: part.expected?.toISOString() ?? "",
      delivered: part.delivered?.toISOString() ?? "",
    })),
    partsOrder: {
      orderNumber: occurrence.partOrderNumber,
      status: occurrence.partOrderStatus,
      statusOrder: occurrence.partOrderStatusOrder,
      occurrenceParts: occurrence.partOrderParts?.map(
        (part: OccurrencePartOrderPart) => ({
          partNumber: JSON.stringify(part.partNumber),
          quantity: part.quantity,
        })
      ),
    },
    partnerId: occurrence.partnerId,
    createdBy: occurrence.createdBy,
    createdByUserProfileId: occurrence.createdByUserProfileId,
    failures: occurrence.failures?.map(OccurrenceFailureDto),
    volksEssentialsPlanContract: occurrence.volksEssentialsPlanContract,
    volksEssentialsPlanRevisionSku: occurrence.volksEssentialsPlanRevisionSku,
  };
};

export const createOccurrenceJson = (occurrence: Occurrence): any => {
  const result = {
    vehicle: {
      chassis: occurrence.vehicleChassis,
      model: occurrence.model ?? occurrence.vehicleModel,
      licencePlace: occurrence.vehicleLicensePlate,
      vehicleType: occurrence.vehicleType,
      vehicleName: occurrence.vehicleName,
      vehicleYear: occurrence.vehicleYear,
      orderDate: occurrence.osDtOpenAt ? toLocalDateTimeString(occurrence.osDtOpenAt) : undefined,
      odometer: occurrence.vehicleOdometer,
      hourMeter: occurrence.vehicleHourmeter,
      customer: occurrence.customer,
      renter: occurrence.renter,
      emissionStandard: occurrence.emissionStandard,
      contractNumber: occurrence.contractNumber,
    },
    driver: {
      name: occurrence.driverName,
      driverLicenseNumber: occurrence.driverLicenseNumber,
      phone: occurrence.driverPhone,
    },
    dealership: {
      dn: occurrence.dealershipDn,
      name: occurrence.dealershipName,
      regional: occurrence.dealershipRegional,
      cell: occurrence.dealershipCell,
      cnpj: occurrence.dealershipUniqueId,
      local: occurrence.dealershipLocation,
      representative: occurrence.dealershipRepresentative,
      cellNumber: occurrence.dealershipCell,
      area: occurrence.dealershipDemarcatedArea,
    },
    observation: occurrence.observation ?? "",
    status: occurrence.status ?? "OPEN",
    criticality: occurrence.criticality ?? 1,
    osNumber: occurrence.osNumber,
    osDtOpenAt: occurrence.osDtOpenAt?.toISOString?.() ?? occurrence.osDtOpenAt,
    createdBy: occurrence.createdBy,
    startDate: occurrence.steps
      ?.find((step) => step.step === OccurrenceStepEnum.CALL)
      ?.dtStart?.toISOString(),
    expectedEndDate: occurrence.steps
      ?.find((step) => step.step === OccurrenceStepEnum.CALL)
      ?.expectedEndDate?.toISOString(),
    estimatedTime:
      occurrence.steps?.find((step) => step.step === OccurrenceStepEnum.CALL)
        ?.estimatedTime ?? "00:00",
    dtcs: occurrence.modules?.map((module: OccurrenceModule) => ({
      module: module.name,
      spn: module.spn,
      fmi: module.fmi,
    })),
    report:
      occurrence.steps?.find((step) => step.step === OccurrenceStepEnum.CALL)
        ?.report ?? "",
    stopped: occurrence.vehicleStopped ?? false,
    occurrenceType: occurrence.occurrenceType ?? "P",
    timeOpenProtocol: occurrence.timeOpenProtocol,
    requestingCountry: occurrence.country,
    hasLink: occurrence.hasLink,
    mainOccurrence: occurrence.mainOccurrence,
    origin: occurrence.source,
    payloadType: occurrence.vehiclePayloadType,
    loadWeight: occurrence.vehicleMaximumPayload,
    criticalLoad: occurrence.vehicleCriticalPayload ?? false,
    partnerId: occurrence.partnerId,
    serviceBaySchedule: occurrence.serviceBaySchedule
      ? {
          startDate: occurrence.serviceBaySchedule.startDate?.toISOString(),
          endDate: occurrence.serviceBaySchedule.endDate?.toISOString(),
          serviceBayId: occurrence.serviceBaySchedule.serviceBay?.id,
          dn: occurrence.dealershipDn,
        }
      : null,
    createdByUserProfileId: occurrence.createdByUserProfileId,
    failures: occurrence.failures?.map(OccurrenceFailureDto),
    volksEssentialsPlanContract: occurrence.volksEssentialsPlanContract,
    volksEssentialsPlanRevisionSku: occurrence.volksEssentialsPlanRevisionSku,
    partOrders: occurrence.partOrders?.map((part: OccurrencePartOrder) => ({
      number: part.number,
      status: part.status,
      date: part.date?.toISOString(),
    })),
    partOrderDeliveryDates: occurrence.partOrderDeliveryDates?.map((part: OccurrencePartOrderDeliveryDate) => ({
      expected: part.expected?.toISOString() ?? "",
      delivered: part.delivered?.toISOString() ?? "",
    })),
    parts: occurrence.partOrderParts?.map((part: OccurrencePartOrderPart) => ({
      partNumber: JSON.stringify(part.partNumber),
      quantity: part.quantity,
    })),
  };
  return result;
};
